import type { ReactNode } from "react";
import { Banner } from "../../components/Banner";
import { Checks } from "../../components/Checks";
import { Pill } from "../../components/Pill";

/*
 * The participant walkthrough (v3's WELCOME_STOPS): the same twelve stops, spoken to them, in order. v3 ran the demo app
 * on a sample participant in a frame; here each stop draws a small static picture of that screen instead. Everything
 * shown is sample data: nothing is fetched and nothing is saved.
 */
export interface WalkthroughStop {
  kicker: string;
  title: string;
  desc: string;
  visual: () => ReactNode;
}

/* A framed "screen" so the sample reads as an illustration, not a live control. */
function Sample({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border-2 border-border bg-surface p-4 sm:p-5">
      <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-text-muted">
        {label} · sample
      </div>
      {children}
    </div>
  );
}

function Row({ children, active = false }: { children: ReactNode; active?: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-[var(--radius-card)] border px-3.5 py-2.5 text-[14px] ${
        active ? "border-accent" : "border-border"
      }`}
    >
      {children}
    </div>
  );
}

function StepBadge({ state, n }: { state: "done" | "active" | "locked"; n: number }) {
  const tone =
    state === "done"
      ? "bg-positive text-white"
      : state === "active"
        ? "bg-accent text-on-accent"
        : "bg-surface-high text-text-muted";
  return (
    <span
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${tone}`}
    >
      {state === "done" ? "✓" : n}
    </span>
  );
}

function Chip({ on = false, children }: { on?: boolean; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-[var(--radius-chip)] border px-3 py-1 text-[13px] font-bold ${
        on ? "border-accent bg-accent text-on-accent" : "border-border bg-surface text-text"
      }`}
    >
      {children}
    </span>
  );
}

function FakeField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[12.5px] font-bold text-text">{label}</div>
      <div className="mt-1 rounded-[var(--radius-control)] border-2 border-field-border bg-surface px-3 py-2 text-[14px]">
        {value}
      </div>
    </div>
  );
}

function GigTile({
  source,
  title,
  meta,
  kind,
}: {
  source: string;
  title: string;
  meta: string;
  kind: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-surface p-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-text-muted">
          {source}
        </span>
        <span className="rounded-full bg-positive-tint px-2.5 py-0.5 text-[11.5px] font-bold text-positive">
          {kind}
        </span>
      </div>
      <div className="text-[15.5px] font-bold leading-[1.2] text-text">{title}</div>
      <div className="text-[13px] text-text-muted">{meta}</div>
      <span className="mt-1 self-start rounded-[var(--radius-button)] bg-accent px-3.5 py-1 text-[13px] font-bold text-on-accent">
        Apply
      </span>
    </div>
  );
}

const HUB_ROWS: { prefix: string; n: number; name: string; state: "done" | "active" | "locked" }[] =
  [
    { prefix: "GIG", n: 1, name: "Position", state: "done" },
    { prefix: "FINANCE", n: 1, name: "Express Interest", state: "done" },
    { prefix: "GIG", n: 2, name: "Package", state: "active" },
    { prefix: "FINANCE", n: 2, name: "Get Your ID Documents Ready", state: "active" },
    { prefix: "GIG", n: 3, name: "Pitch", state: "locked" },
  ];

const PLATFORMS = ["Prodevs", "Growwr", "TalentApply", "Contra", "Upwork"];

export const WALKTHROUGH_STOPS: WalkthroughStop[] = [
  {
    kicker: "Your steps",
    title: "Your road to paid work",
    desc: "Everything happens on one page: six steps to the Gig Board, with an Access to Finance step beside each. Tap any step to open it.",
    visual: () => (
      <Sample label="Your steps">
        <div className="mb-3 h-2 overflow-hidden rounded-full bg-surface-high">
          <i className="block h-full w-[18%] bg-accent" />
        </div>
        <div className="flex flex-col gap-2">
          {HUB_ROWS.map((row) => (
            <Row
              key={`${row.prefix}${row.n}`}
              active={row.state === "active" && row.prefix === "GIG"}
            >
              <StepBadge state={row.state} n={row.n} />
              <span className="flex-1">
                <span className="block text-[10.5px] font-bold uppercase tracking-[0.08em] text-text-muted">
                  {row.prefix} · Step {row.n}
                </span>
                <span className="block font-bold">{row.name}</span>
              </span>
              <span className="text-[12px] font-bold text-text-muted">
                {row.state === "done" ? "Done" : row.state === "active" ? "Do this" : "Locked"}
              </span>
            </Row>
          ))}
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Step 1 · Position",
    title: "Get live on 5 partner platforms",
    desc: "Write one line about what you do and who it's for, then sign up on at least 5 partner platforms and paste your profile links. Each link is checked, so use your real profile page.",
    visual: () => (
      <Sample label="Step 1 · Position">
        <FakeField
          label="Your one line"
          value="I design logos and brand kits for small food businesses in Lagos."
        />
        <div className="mt-3 flex flex-col gap-1.5">
          {PLATFORMS.map((name, index) => (
            <Row key={name}>
              <span className="flex-1 font-bold">{name}</span>
              <Pill ok={index < 3} okText="Link checked" noText="Paste link" />
            </Row>
          ))}
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Step 2 · Package",
    title: "Build a portfolio clients trust",
    desc: "You'll see a full sample portfolio for your track. Then add your own portfolio link and upload your CV. Both are required.",
    visual: () => (
      <Sample label="Step 2 · Package">
        <div className="rounded-[var(--radius-card)] bg-surface-high p-3.5">
          <div className="text-[15px] font-bold">Adaeze Nwosu · Graphic designer</div>
          <div className="mt-2 grid grid-cols-3 gap-2" aria-hidden="true">
            <span className="h-14 rounded bg-accent/80" />
            <span className="h-14 rounded bg-warning/70" />
            <span className="h-14 rounded bg-positive/60" />
          </div>
          <div className="mt-2 text-[13px] text-text-muted">
            3 case studies · client results · clear prices
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-1.5">
          <Row>
            <span className="flex-1 font-bold">Your portfolio link</span>
            <Pill ok />
          </Row>
          <Row>
            <span className="flex-1 font-bold">Your CV</span>
            <Pill ok={false} />
          </Row>
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Step 2 · Package",
    title: "A profile photo that earns trust",
    desc: "Clear examples of what works and what doesn't. Your photo is the first thing a client sees.",
    visual: () => (
      <Sample label="Profile photo">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-[var(--radius-card)] border-2 border-positive p-3 text-[13.5px]">
            <b className="text-positive">✓ Works</b>
            <p className="m-0 mt-1 text-text-muted">
              Your face, clear and centred, good light, plain background.
            </p>
          </div>
          <div className="rounded-[var(--radius-card)] border-2 border-negative p-3 text-[13.5px]">
            <b className="text-negative">× Doesn't</b>
            <p className="m-0 mt-1 text-text-muted">
              Blurry, a group shot, sunglasses, or a logo instead of you.
            </p>
          </div>
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Step 3 · Pitch",
    title: "Weak bids don't count",
    desc: "You'll practise on realistic jobs. A one-line bid like this is rejected, with the reasons, so you can fix it. You need ten strong bids.",
    visual: () => (
      <Sample label="Step 3 · Pitch">
        <FakeField label="Your bid" value="Hello, I can do this job. Please hire me." />
        <Checks
          checks={[
            {
              label: "Speaks to the client's problem",
              pass: false,
              fail: "say what they asked for",
            },
            { label: "Shows proof", pass: false, fail: "link a piece of work like theirs" },
            { label: "Clear next step", pass: false, fail: "offer a time or a first draft" },
          ]}
        />
        <div className="mt-3 text-[13px] text-text-muted">Strong bids: 3 of 10</div>
      </Sample>
    ),
  },
  {
    kicker: "Step 4 · Verify",
    title: "Become Work-Enabled",
    desc: "Set up how you get paid, and upload a screenshot of your identity check (KYC) on one of your platforms. With both, you're Work-Enabled and get your Launch Plan.",
    visual: () => (
      <Sample label="Step 4 · Verify">
        <div className="flex flex-col gap-1.5">
          <Row>
            <span className="flex-1 font-bold">How you get paid</span>
            <Pill ok />
          </Row>
          <Row>
            <span className="flex-1 font-bold">KYC screenshot</span>
            <Pill ok />
          </Row>
        </div>
        <Banner tone="good">✓ You're Work-Enabled. Open your Launch Plan</Banner>
      </Sample>
    ),
  },
  {
    kicker: "Step 5 · Certificate",
    title: "Your certificate unlocks the Gig Board",
    desc: "Upload your Growth Pathway certificate. That completes the pathway.",
    visual: () => (
      <Sample label="Step 5 · Certificate">
        <Row>
          <span className="flex-1 font-bold">Growth Pathway certificate</span>
          <Pill ok okText="Uploaded" />
        </Row>
        <Banner tone="good">✓ Growth Pathway complete. Open the Gig Board</Banner>
      </Sample>
    ),
  },
  {
    kicker: "Step 6 · Gig Board",
    title: "Thousands of real gigs",
    desc: "Fresh gigs and jobs every day from 16 sources, all open to Nigerians. Apply opens the original listing.",
    visual: () => (
      <Sample label="Gig Board">
        <div className="grid gap-2.5 sm:grid-cols-2">
          <GigTile
            source="Upwork"
            title="Logo and brand kit for a bakery"
            meta="Remote · posted today"
            kind="Gig"
          />
          <GigTile
            source="Jobberman"
            title="Junior graphic designer"
            meta="Lagos · posted yesterday"
            kind="Job"
          />
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Step 6 · Gig Board",
    title: "Your skill, and more",
    desc: "Filter by your skill, or explore transferable roles: virtual assistant, customer experience, sales, call centre and AI training.",
    visual: () => (
      <Sample label="Gig Board filters">
        <div className="flex flex-wrap gap-2">
          <Chip>Graphic design</Chip>
          <Chip on>Virtual assistant</Chip>
          <Chip>Customer experience</Chip>
          <Chip>Sales</Chip>
          <Chip>Call centre</Chip>
          <Chip>AI training</Chip>
        </div>
        <div className="mt-3">
          <GigTile
            source="Remotive"
            title="Virtual assistant, part-time"
            meta="Remote · posted today"
            kind="Gig"
          />
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Every Friday",
    title: "A one-minute check-in",
    desc: "Each Friday you'll tell us how your applications went, and about any paid work. Turn on reminders and we'll nudge you, including each evening you go a day without progress.",
    visual: () => (
      <Sample label="Friday check-in">
        <div className="text-[15px] font-bold">How did "Logo and brand kit for a bakery" go?</div>
        <div className="mt-2.5 flex flex-wrap gap-2">
          <Chip>No reply yet</Chip>
          <Chip on>They replied</Chip>
          <Chip>Got the work</Chip>
          <Chip>Not chosen</Chip>
        </div>
        <div className="mt-3 text-[13.5px] text-text-muted">Any other paid work this week?</div>
      </Sample>
    ),
  },
  {
    kicker: "Report a win",
    title: "Every paid win counts",
    desc: "Landed paid work, anywhere? Report it here, with proof if you have it.",
    visual: () => (
      <Sample label="Report a win">
        <div className="flex flex-col gap-2.5">
          <FakeField label="What was the work?" value="Menu design for a restaurant" />
          <FakeField label="Amount paid" value="₦45,000" />
          <Row>
            <span className="flex-1 font-bold">Proof (invoice or screenshot)</span>
            <Pill ok okText="Added" noText="Optional" />
          </Row>
        </div>
      </Sample>
    ),
  },
  {
    kicker: "Access to Finance",
    title: "Ready for a device, insurance and a pension",
    desc: "Short checklists get you ready for the provider's own checks. You never enter ID numbers or documents here.",
    visual: () => (
      <Sample label="Access to Finance">
        <div className="flex flex-col gap-1.5">
          {[
            ["Device financing", true],
            ["Health insurance", true],
            ["Micro-pension", false],
          ].map(([name, on]) => (
            <Row key={name as string} active={on as boolean}>
              <span
                aria-hidden="true"
                className={`inline-flex h-5 w-5 items-center justify-center rounded border-2 text-[12px] font-bold ${
                  on ? "border-accent bg-accent text-on-accent" : "border-field-border"
                }`}
              >
                {on ? "✓" : ""}
              </span>
              <span className="flex-1 font-bold">{name as string}</span>
            </Row>
          ))}
        </div>
      </Sample>
    ),
  },
];
